import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography,
  CardMedia,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  Button,
  Stack,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import { 
  NavigateNext, 
  NavigateBefore,
  FirstPage,
  LastPage,
  CheckCircleOutline
} from '@mui/icons-material';
import scenarios from '../data/scenarios.json';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { ENGLISH_SCENARIOS } from '../i18n/scenarios';

const TrainingScenarios = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [open, setOpen] = useState(false);
  const [handbookOpen, setHandbookOpen] = useState(false);
  const [handbookFlow, setHandbookFlow] = useState([]);
  const [handbookTranslations, setHandbookTranslations] = useState({});
  const [isHandbookLoading, setIsHandbookLoading] = useState(false);
  const [handbookError, setHandbookError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const navigate = useNavigate();
  const location = useLocation();
  const { tr, t, isEnglish } = useLanguage();
  const [filteredScenarios, setFilteredScenarios] = useState([]);
  const defaultCategory = '銀行理專';
  
  // Move getCategory outside useEffect and wrap in useMemo
  const getCategory = React.useMemo(() => {
    return () => {
      const searchParams = new URLSearchParams(location.search);
      return searchParams.get('category') || defaultCategory;
    };
  }, [location.search]);

  // Filter scenarios based on category
  useEffect(() => {
    const currentCategory = getCategory();
    const filtered = scenarios.filter(scenario => scenario.category === currentCategory);
    setFilteredScenarios(filtered);
    setCurrentPage(1);
  }, [getCategory]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredScenarios.length / itemsPerPage);

  // Get current scenarios for the page
  const getCurrentScenarios = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredScenarios.slice(startIndex, endIndex);
  };

  // Handle page changes
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Adjust visible page numbers based on screen size and total pages
  const getPageNumbers = () => {
    if (totalPages <= 1) return [1];
    
    if (isMobile) {
      // Show current page and last page only on mobile
      return currentPage === totalPages ? 
        [totalPages - 1, totalPages] : 
        [currentPage, currentPage < totalPages - 1 ? currentPage + 1 : totalPages];
    } else if (isTablet) {
      // Show first page, current/adjacent page, and last page on tablet
      const numbers = [1];
      if (currentPage > 2) numbers.push('...');
      if (currentPage !== 1 && currentPage !== totalPages) numbers.push(currentPage);
      if (currentPage < totalPages - 1) numbers.push('...');
      if (totalPages !== 1) numbers.push(totalPages);
      return numbers;
    }
    
    // Desktop view
    const numbers = [1];
    if (currentPage > 2) numbers.push('...');
    if (currentPage !== 1 && currentPage !== totalPages) numbers.push(currentPage);
    if (currentPage < totalPages - 1) numbers.push('...');
    if (totalPages !== 1) numbers.push(totalPages);
    return numbers;
  };

  const handleCardClick = (scenario) => {
    setSelectedScenario(scenario);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedScenario(null);
  };

  const handleOpenHandbook = () => {
    setOpen(false);
    setHandbookOpen(true);
  };

  const handleCloseHandbook = () => {
    setHandbookOpen(false);
    setSelectedScenario(null);
    setHandbookFlow([]);
    setHandbookError('');
  };

  const handleStartPractice = (id) => {
    setOpen(false);
    setHandbookOpen(false);
    setSelectedScenario(null);
    // Check if id is already in 'caseX' format
    const scenarioKey = id.toString().startsWith('case') ? id : `case${id}`;
    navigate(`/chat/${scenarioKey}`);
  };

  useEffect(() => {
    if (!handbookOpen || !selectedScenario) return undefined;
    let cancelled = false;

    const loadHandbook = async () => {
      setIsHandbookLoading(true);
      setHandbookError('');
      try {
        const flowPromise = axios.get(`/flowcharts/json/${selectedScenario.id}_flow.json`);
        let translationsPromise = Promise.resolve({});

        if (isEnglish) {
          const cacheKey = `practiceLocalization:v2:${selectedScenario.id}`;
          let cached = null;
          try {
            cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
          } catch (cacheError) {
            localStorage.removeItem(cacheKey);
          }

          if (cached && typeof cached === 'object') {
            translationsPromise = Promise.resolve(cached);
          } else {
            translationsPromise = axios.post(`${API_URL}/api/practice-localization`, {
              scenario_id: selectedScenario.id,
              language: 'en'
            }).then((response) => {
              const translations = response.data?.translations || {};
              try {
                localStorage.setItem(cacheKey, JSON.stringify(translations));
              } catch (cacheError) {
                console.warn('Could not cache handbook localization:', cacheError);
              }
              return translations;
            });
          }
        }

        const [flowResponse, translations] = await Promise.all([flowPromise, translationsPromise]);
        if (!cancelled) {
          setHandbookFlow(Array.isArray(flowResponse.data) ? flowResponse.data : []);
          setHandbookTranslations(translations);
        }
      } catch (error) {
        console.error('Error loading coaching guide:', error);
        if (!cancelled) {
          setHandbookError(tr('目前無法載入建議手冊，請稍後再試。', 'The coaching guide could not be loaded. Please try again.'));
        }
      } finally {
        if (!cancelled) setIsHandbookLoading(false);
      }
    };

    loadHandbook();
    return () => {
      cancelled = true;
    };
  }, [handbookOpen, selectedScenario, isEnglish, tr]);

  const localizeHandbook = (value) => {
    if (!isEnglish || typeof value !== 'string') return value;
    return handbookTranslations[value] || value;
  };

  const scenarioField = (scenario, field) => {
    if (!isEnglish) return scenario?.[field] || '';
    return ENGLISH_SCENARIOS[scenario?.id]?.[field] || scenario?.[field] || '';
  };

  const difficultyLabel = (difficulty) => {
    if (difficulty === 'easy') return tr('初級', 'Easy');
    if (difficulty === 'medium') return tr('中級', 'Intermediate');
    if (difficulty === 'hard') return tr('高級', 'Advanced');
    return tr('初級', 'Easy');
  };

  // Update breadcrumb to show current category
  const categoryLabel = isEnglish
    ? t(`category.${getCategory()}`)
    : (getCategory() === '銀行理專' ? '理專' : '電銷');

  return (
    <Box 
      sx={{ 
        p: { xs: 2, sm: 3, md: 4 }, // Responsive padding
        backgroundColor: '#fff', 
        minHeight: '100vh'
      }}
    >
      {/* Updated Header with category */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 2, sm: 3, md: 4 }, // Responsive margin
          flexWrap: 'wrap'
        }}
      >
        <Typography 
          sx={{ 
            color: '#666',
            fontSize: { xs: '0.875rem', sm: '1rem' }, // Responsive font size
            '& span': {
              color: '#999',
              mx: 0.5,
            }
          }}
        >
          Fubon <span>/</span> {categoryLabel} <span>/</span> {tr('陪練課程', 'Training Courses')}
        </Typography>
      </Box>

      {/* Grid of cards */}
      <Grid 
        container 
        spacing={{ xs: 2, sm: 2, md: 3 }} // Responsive grid spacing
      >
        {getCurrentScenarios().map((scenario) => (
          <Grid 
            item 
            xs={12}          // 1 card per row on mobile
            sm={6}           // 2 cards per row on tablet
            md={4}           // 3 cards per row on small desktop
            lg={3}           // 4 cards per row on large desktop
            key={scenario.id}
          >
            <Card 
              onClick={() => handleCardClick(scenario)}
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: { xs: 1, sm: 2 }, // Responsive border radius
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardMedia
                component="img"
                height={{ xs: '180', sm: '200' }} // Responsive image height
                image={scenario.image}
                alt={scenarioField(scenario, 'name')}
                sx={{
                  objectFit: 'cover',
                }}
              />
              <CardContent 
                sx={{ 
                  p: { xs: 1.5, sm: 2 } // Responsive padding
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 500,
                    mb: 0.5,
                    fontSize: { xs: '0.9rem', sm: '1rem' } // Responsive font size
                  }}
                >
                  {scenarioField(scenario, 'name')}{isEnglish ? ', ' : '，'}{scenario.age}
                  <Box component="span" sx={{ 
                    ml: 1, 
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    color: 'text.secondary',
                    fontWeight: 400,
                    display: 'inline-block'
                  }}>
                    {difficultyLabel(scenario.difficulty)}
                  </Box>
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }, // Responsive font size
                    lineHeight: 1.5
                  }}
                >
                  {scenarioField(scenario, 'title')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Show message if no scenarios found */}
      {filteredScenarios.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          {tr('此分類沒有可用的案例', 'No scenarios are available in this category.')}
        </Typography>
      )}

      {/* Scenario Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        {selectedScenario && (
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box
                  component="img"
                  src={selectedScenario.image}
                  alt={scenarioField(selectedScenario, 'name')}
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    objectFit: 'cover'
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {scenarioField(selectedScenario, 'name')}{isEnglish ? ', ' : '，'}{selectedScenario.age}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {tr('銀行業', 'Banking')} {tr('難度', 'Difficulty')}: {difficultyLabel(selectedScenario.difficulty)}
                  </Typography>
                  <Typography variant="body1">
                    {scenarioField(selectedScenario, 'title')}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" sx={{ my: 2, whiteSpace: 'pre-line' }}>
                {scenarioField(selectedScenario, 'description')}
              </Typography>

              <Stack 
                direction="row" 
                spacing={2} 
                sx={{ mt: 2 }}
              >
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={handleOpenHandbook}
                  sx={{ borderRadius: 2 }}
                >
                  {tr('建議手冊', 'Coaching Guide')}
                </Button>
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={() => handleStartPractice(selectedScenario.id)}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: '#00a3a3',
                    '&:hover': {
                      backgroundColor: '#008080'
                    }
                  }}
                >
                  {tr('開始練習', 'Start Practice')}
                </Button>
              </Stack>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={handbookOpen}
        onClose={handleCloseHandbook}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '88vh' } }}
      >
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" fontWeight={800}>
            {tr('建議手冊', 'Coaching Guide')}
          </Typography>
          {selectedScenario && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {scenarioField(selectedScenario, 'title')}
            </Typography>
          )}
          <Divider sx={{ my: 2 }} />

          {isHandbookLoading ? (
            <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography color="text.secondary">
                {tr('正在載入建議手冊...', 'Loading coaching guide…')}
              </Typography>
            </Box>
          ) : handbookError ? (
            <Alert severity="error">{handbookError}</Alert>
          ) : (
            <Stack spacing={3}>
              {handbookFlow.map((stage) => (
                <Box key={stage.id}>
                  <Typography variant="h6" color="primary.main" fontWeight={800} sx={{ mb: 1.5 }}>
                    {localizeHandbook(stage.text)}
                  </Typography>
                  <Stack spacing={2}>
                    {(stage.nodes || []).map((node) => (
                      <Box key={node.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={800}>
                          {localizeHandbook(node.text)}
                        </Typography>
                        {node.goal && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {tr('目標：', 'Goal:')} {localizeHandbook(node.goal)}
                          </Typography>
                        )}
                        {(node.checkpoints || []).length > 0 && (
                          <>
                            <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }}>
                              {tr('建議重點', 'Recommended Talking Points')}
                            </Typography>
                            <List dense disablePadding sx={{ mt: 0.5 }}>
                              {node.checkpoints.map((checkpoint, index) => {
                                const checkpointText = typeof checkpoint === 'string' ? checkpoint : checkpoint.text;
                                return (
                                  <ListItem key={`${node.id}-${index}`} disableGutters alignItems="flex-start">
                                    <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                                      <CheckCircleOutline color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={localizeHandbook(checkpointText)} />
                                  </ListItem>
                                );
                              })}
                            </List>
                          </>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
            <Button variant="outlined" fullWidth onClick={handleCloseHandbook}>
              {tr('關閉', 'Close')}
            </Button>
            {selectedScenario && (
              <Button variant="contained" fullWidth onClick={() => handleStartPractice(selectedScenario.id)}>
                {tr('開始練習', 'Start Practice')}
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Updated Pagination */}
      {filteredScenarios.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: 3, sm: 4 },
            mb: { xs: 2, sm: 2 },
            gap: { xs: 0.5, sm: 1 },
            flexWrap: 'wrap'
          }}
        >
          <IconButton 
            size={isMobile ? "small" : "medium"}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' },
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            <FirstPage fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <IconButton 
            size={isMobile ? "small" : "medium"}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <NavigateBefore fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          
          {getPageNumbers().map((page, index) => (
            <Box
              key={index}
              onClick={() => page !== '...' && handlePageChange(page)}
              sx={{
                width: { xs: 24, sm: 28 },
                height: { xs: 24, sm: 28 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                cursor: page === '...' ? 'default' : 'pointer',
                backgroundColor: page === currentPage ? '#2196f3' : 'transparent',
                color: page === currentPage ? '#fff' : '#666',
                '&:hover': {
                  backgroundColor: page === currentPage ? '#2196f3' : 
                                page === '...' ? 'transparent' : '#f5f5f5'
                },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              {page}
            </Box>
          ))}

          <IconButton 
            size={isMobile ? "small" : "medium"}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <NavigateNext fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <IconButton 
            size={isMobile ? "small" : "medium"}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' },
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            <LastPage fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default TrainingScenarios;
